/* eslint-disable @altermaind/you-shall-not-pass */
import { useCallback, useEffect, useRef, useState } from 'react';

type BackoffMode = 'none' | 'exponential';

/**
 * Parameters received by the polling task on every iteration.
 *
 * @template ApiResponse - Shape of the response your task produces.
 * @template CallbackParams - Shape of the optional args you pass to `start(args)` / `setArgs(args)`.
 */
export type UsePollingTaskParams<ApiResponse, CallbackParams> = {
  /** Abort the current iteration (cancels any in-flight request the task may start). */
  signal: AbortSignal;
  /**
   * Arguments persisted across iterations. Set when calling `start(args)` and
   * updatable with `setArgs(args)` (effective from the next iteration).
   */
  args: CallbackParams | undefined;
  /** Report a successful result back to the hook (required for scheduling the next iteration). */
  onResult: (response: ApiResponse) => void;
  /** Report an error back to the hook (used for retry logic / stop-on-error). */
  onError: (err: unknown) => void;
};

/**
 * Configuration for the polling behavior.
 *
 * @template T - ApiResponse type.
 * @template A - CallbackParams type.
 */
export type UsePollingOptions<T, A> = {
  /**
   * Base delay in milliseconds used to schedule the next iteration **after** an iteration completes.
   * This hook uses a fixed-delay model. With `backoff: "none"`, the delay is constant.
   * Default: 1500.
   */
  intervalMs?: number;
  /**
   * Backoff strategy for increasing the delay as attempts grow:
   *  - "none": constant delay = `intervalMs`
   *  - "exponential": `min(maxIntervalMs, intervalMs * 2^(attempt-1))`
   * Default: "exponential".
   */
  backoff?: BackoffMode;
  /** Upper bound (ms) applied when `backoff: "exponential"`. Default: 10_000. */
  maxIntervalMs?: number;
  /** Maximum number of iterations before stopping (1-based). Omit for infinite. */
  maxAttempts?: number;
  /**
   * When `true`, errors reported via `onError` schedule another attempt after the computed delay.
   * When `false`, the hook stops on the first error. Default: true.
   */
  retryOnError?: boolean;
  /**
   * If `true`, invokes `start()` automatically on mount (with the current args, initially `undefined`).
   * Default: false.
   */
  startImmediate?: boolean;
  /**
   * If `true`, pauses polling when the tab becomes hidden and resumes on visibility.
   * Args are preserved across pause/resume. Default: true.
   */
  pauseInBackground?: boolean;
  /**
   * Stop condition evaluated **after** a successful `onResult`. If it returns `true`, polling stops.
   * Signature: `(result, attemptNumber, args) => boolean`.
   */
  stopWhen: (result: T, attempt: number, args: A | undefined) => boolean;
  /**
   * Optional external stop condition evaluated **before** executing each iteration.
   * If it returns `true`, the iteration is skipped and polling stops.
   */
  stopIf?: (args: A | undefined) => boolean;
  /**
   * Optional external AbortSignal that, when aborted, stops the entire polling loop.
   * It also aborts any in-flight iteration.
   */
  signal?: AbortSignal;
};

/**
 * React hook for **callback-based polling** with cancellation, exponential backoff, and safe cleanup.
 *
 * ### Timing model
 * - The first iteration is scheduled immediately on `start()` (delay 0).
 * - Subsequent iterations use a **fixed-delay** model: the next attempt is scheduled
 *   after an iteration finishes, waiting `computeDelay(attempt)`.
 * - With `backoff: "exponential"`, delay grows as attempts increase and is capped at `maxIntervalMs`.
 *
 * ### Concurrency & cancellation
 * - At most one iteration is in-flight at a time.
 * - Starting a new iteration aborts the previous one (via `AbortController`).
 * - An external `signal` (if provided) stops the loop and aborts the current iteration.
 *
 * ### Visibility
 * - With `pauseInBackground: true`, polling stops when the document is hidden and resumes (same args) when visible again.
 *
 * ### Data flow
 * - Your `task` **must** call either `onResult(result)` or `onError(error)` exactly once per iteration.
 * - `stopWhen(result, attempt, args)` decides whether to stop after a successful result.
 * - Errors follow `retryOnError`: retry after delay if `true`, otherwise stop.
 *
 * @template ApiResponse - The result produced by your task.
 * @template CallbackParams - The args you wish to pass at `start(args)` and optionally update later.
 *
 * @param task A function invoked each iteration: it receives `{ signal, args, onResult, onError }`.
 * @param options See {@link UsePollingOptions}.
 *
 * @returns Controls and state:
 * - `data`: last successful result (or `undefined` if none yet)
 * - `error`: last error (if any)
 * - `isRunning`: whether the loop is currently active
 * - `attempts`: number of started iterations (1-based)
 * - `start(args?)`: begin polling with optional args (first iteration scheduled immediately)
 * - `stop()`: stop polling and abort any in-flight iteration
 * - `reset()`: clear `data`, `error`, and `attempts` (does not start/stop the loop)
 * - `setArgs(args)`: update args for subsequent iterations
 *
 * @example
 * // Minimal example (fetch every 2s until result.done === true)
 * const { data, start, stop } = usePolling(
 *   ({ signal, onResult, onError }) => {
 *     fetch('/api/task', { signal })
 *       .then(r => r.json())
 *       .then(onResult)
 *       .catch(onError);
 *   },
 *   {
 *     intervalMs: 2000,
 *     backoff: 'none',
 *     stopWhen: (res) => res.done === true,
 *   }
 * );
 * useEffect(() => { start(); }, [start]);
 */
export const usePolling = <ApiResponse, CallbackParams = undefined>(
  task: (p: UsePollingTaskParams<ApiResponse, CallbackParams>) => void,
  {
    intervalMs = 1500,
    backoff = 'exponential',
    maxIntervalMs = 10_000,
    maxAttempts,
    retryOnError = true,
    startImmediate = false,
    pauseInBackground = true,
    stopWhen,
    stopIf,
    signal,
  }: UsePollingOptions<ApiResponse, CallbackParams>,
) => {
  const [data, setData] = useState<ApiResponse | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [isRunning, setIsRunning] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // === Refs to avoid stale closures / races ===
  const argsRef = useRef<CallbackParams | undefined>(undefined);
  const taskRef = useRef(task);
  taskRef.current = task;

  const optsRef = useRef({
    intervalMs,
    backoff,
    maxIntervalMs,
    maxAttempts,
    retryOnError,
    pauseInBackground,
    stopWhen,
    stopIf,
  });
  optsRef.current = {
    intervalMs,
    backoff,
    maxIntervalMs,
    maxAttempts,
    retryOnError,
    pauseInBackground,
    stopWhen,
    stopIf,
  };

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightAbortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  const runningRef = useRef(false); // avoids first-run race
  const runOnceRef = useRef<() => void>(() => {}); // always schedule the newest runOnce
  const wasRunningBeforeHideRef = useRef(false);
  const unmountedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const cancelInFlight = () => {
    if (inFlightAbortRef.current) {
      inFlightAbortRef.current.abort();
      inFlightAbortRef.current = null;
    }
  };

  const computeDelay = (attempt: number) => {
    const { intervalMs: base, backoff: mode, maxIntervalMs: maxMs } = optsRef.current;
    if (mode === 'none') return base;
    // exponential backoff: base * 2^(attempt-1), capped at maxMs
    return Math.min(maxMs, base * 2 ** Math.max(0, attempt - 1));
  };

  const scheduleNext = (delay: number) => {
    clearTimer();
    timerRef.current = setTimeout(() => runOnceRef.current(), delay);
  };

  const stop = useCallback(() => {
    runningRef.current = false;
    setIsRunning(false);
    clearTimer();
    cancelInFlight();
  }, []);

  const reset = useCallback(() => {
    setData(undefined);
    setError(undefined);
    setAttempts(0);
  }, []);

  const setArgs = useCallback((nextArgs: CallbackParams | undefined) => {
    argsRef.current = nextArgs;
  }, []);

  const runOnce = useCallback(() => {
    if (unmountedRef.current || !runningRef.current) return;

    const {
      maxAttempts: maxAtt,
      stopIf: shouldStop,
      retryOnError: shouldRetryOnError,
      stopWhen: shouldStopWhen,
    } = optsRef.current;
    const currArgs = argsRef.current;

    // External stop gate (checked before executing the task)
    if (shouldStop?.(currArgs)) {
      stop();
      return;
    }

    const nextAttempt = attempts + 1;
    if (maxAtt && nextAttempt > maxAtt) {
      stop();
      return;
    }
    setAttempts(nextAttempt);

    // Ensure previous iteration is cancelled
    cancelInFlight();
    const controller = new AbortController();
    inFlightAbortRef.current = controller;

    const onExternalAbort = () => controller.abort();
    signal?.addEventListener('abort', onExternalAbort, { once: true });

    // eslint-disable-next-line no-plusplus
    const myRunId = ++runIdRef.current;
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener('abort', onExternalAbort);
      if (inFlightAbortRef.current === controller) {
        inFlightAbortRef.current = null;
      }
    };

    const onResult = (result: ApiResponse) => {
      if (settled) return;
      settled = true;
      if (unmountedRef.current || myRunId !== runIdRef.current) {
        cleanup();
        return;
      }

      setData(result);
      setError(undefined);

      if (shouldStopWhen(result, nextAttempt, currArgs)) {
        stop();
      } else {
        scheduleNext(computeDelay(nextAttempt));
      }
      cleanup();
    };

    const onError = (err: unknown) => {
      if (settled) return;
      settled = true;
      if (unmountedRef.current || myRunId !== runIdRef.current) {
        cleanup();
        return;
      }

      setError(err);
      if (shouldRetryOnError) {
        scheduleNext(computeDelay(nextAttempt));
      } else {
        stop();
      }
      cleanup();
    };

    // Execute the task (sync or async via callback).
    try {
      taskRef.current({ signal: controller.signal, args: currArgs, onResult, onError });
    } catch (err) {
      onError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempts, signal, stop]);

  const start = useCallback((startArgs?: CallbackParams) => {
    if (runningRef.current) return;
    argsRef.current = startArgs;
    runningRef.current = true; // set before scheduling
    setIsRunning(true);
    // Note: we don't reset `attempts` automatically; call `reset()` if you need to.
    scheduleNext(0); // fire first iteration immediately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep runOnce ref fresh
  useEffect(() => {
    runOnceRef.current = runOnce;
  }, [runOnce]);

  // Pause/resume with page visibility
  useEffect(() => {
    const onVisibility = () => {
      if (!optsRef.current.pauseInBackground) return;
      if (document.hidden) {
        wasRunningBeforeHideRef.current = runningRef.current;
        if (runningRef.current) stop();
      } else if (wasRunningBeforeHideRef.current) {
        wasRunningBeforeHideRef.current = false;
        start(argsRef.current); // resume with same args
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [start, stop]);

  // External abort → stop
  useEffect(() => {
    if (!signal) {
      return undefined;
    }
    const onAbort = () => stop();
    signal.addEventListener('abort', onAbort);
    return () => {
      signal.removeEventListener('abort', onAbort);
    };
  }, [signal, stop]);

  // Optional auto-start on mount
  useEffect(() => {
    if (startImmediate) {
      start(argsRef.current);
    }

    return () => {
      unmountedRef.current = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    error,
    isRunning,
    attempts,
    start,
    stop,
    reset,
    setArgs,
  };
};
